import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DayritLogo } from '../components/ui';
import { offlineStorage, habitOfflineStorage } from '../utils/offlineStorage';
import { authApi } from '../api';

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.7s linear infinite', flexShrink: 0 }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );
}

function FieldInput({ label, type = 'text', placeholder, value, onChange, error, autoFocus, hint, rightEl }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type={type} placeholder={placeholder} value={value} onChange={onChange} autoFocus={autoFocus}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: rightEl ? '11px 42px 11px 14px' : '11px 14px',
            fontSize: 14, fontFamily: 'inherit',
            border: `1.5px solid ${error ? '#EF4444' : focused ? 'var(--accent)' : 'var(--border)'}`,
            borderRadius: 10, background: 'var(--input-bg)', color: 'var(--text)', outline: 'none',
            boxShadow: focused && !error ? '0 0 0 3px var(--accent-ring)' : 'none',
            transition: 'all 0.15s',
          }}
        />
        {rightEl && <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>{rightEl}</div>}
      </div>
      {error && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4, fontWeight: 500 }}>{error}</p>}
      {hint && !error && <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{hint}</p>}
    </div>
  );
}

function PasswordInput({ label, placeholder, value, onChange, error, hint, autoFocus }) {
  const [show, setShow] = useState(false);
  return (
    <FieldInput label={label} type={show ? 'text' : 'password'} placeholder={placeholder}
      value={value} onChange={onChange} error={error} hint={hint} autoFocus={autoFocus}
      rightEl={
        <button type="button" onClick={() => setShow(p => !p)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, display: 'flex' }}>
          {show
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          }
        </button>
      }
    />
  );
}

function buildOfflineLabel(taskCount, habitCount) {
  const parts = [];
  if (taskCount > 0) parts.push(`${taskCount} task${taskCount !== 1 ? 's' : ''}`);
  if (habitCount > 0) parts.push(`${habitCount} habit${habitCount !== 1 ? 's' : ''}`);
  return parts.join(' & ');
}

export default function RegisterPage() {
  const navigate = useNavigate();

  const [form,    setForm]    = useState({ name: '', email: '', password: '', password_confirmation: '' });
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});

  const offlineTaskCount  = offlineStorage.count();
  const offlineHabitCount = habitOfflineStorage.count();
  const offlineCount      = offlineTaskCount + offlineHabitCount;
  const offlineLabel      = buildOfflineLabel(offlineTaskCount, offlineHabitCount);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim())                            e.name                 = 'Name is required';
    if (!form.email.trim())                           e.email                = 'Email is required';
    if (form.password.length < 8)                     e.password             = 'Minimum 8 characters';
    if (form.password !== form.password_confirmation) e.password_confirmation = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true); setErrors({});
    try {
      // Call authApi directly — avoids AuthContext.register() signature ambiguity
      const res  = await authApi.register({
        name:                  form.name,
        email:                 form.email,
        password:              form.password,
        password_confirmation: form.password_confirmation,
      });

      const { token, email_verified } = res.data;

      // Store token so the app is usable immediately
      if (token) localStorage.setItem('dayrit_token', token);

      // Store email for the verify page
      localStorage.setItem('dayrit_pending_email', form.email);

      if (email_verified === false) {
        // Redirect to verify email page
        navigate('/verify-email', {
          replace: true,
          state:   { email: form.email },
        });
      } else {
        // Email already verified (or verification not required) — go straight to app
        window.location.replace('/app');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) {
        // Map Laravel validation errors: { email: ['msg'] } → { email: 'msg' }
        setErrors(Object.fromEntries(
          Object.entries(data.errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])
        ));
      } else {
        setErrors({ general: data?.message || 'Registration failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(16px,4vw,40px)', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      <div style={{ width: '100%', maxWidth: 440 }}>

        {/* Wordmark */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, marginBottom: 32 }}>
          <DayritLogo size={32} />
          <span style={{ fontSize: 18, fontWeight: 800, background: 'var(--brand-grad)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Dayrit</span>
        </div>

        {/* Card */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 'clamp(24px,5vw,36px)', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>

          <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px', marginBottom: 4, textAlign: 'center' }}>
            Create your account
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', marginBottom: 22 }}>
            Free forever · No credit card required
          </p>

          {offlineCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', borderRadius: 10, background: 'var(--accent-light)', border: '1px solid var(--accent-ring)', marginBottom: 18 }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>☁️</span>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>
                {offlineLabel} will sync to your new account
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FieldInput label="Full Name" placeholder="Jane Smith"
              value={form.name} onChange={e => set('name', e.target.value)}
              error={errors.name} autoFocus />

            <FieldInput label="Email" type="email" placeholder="you@example.com"
              value={form.email} onChange={e => set('email', e.target.value)}
              error={errors.email} />

            <PasswordInput label="Password" placeholder="Min. 8 characters"
              value={form.password} onChange={e => set('password', e.target.value)}
              error={errors.password} hint="At least 8 characters" />

            <PasswordInput label="Confirm password" placeholder="Repeat your password"
              value={form.password_confirmation} onChange={e => set('password_confirmation', e.target.value)}
              error={errors.password_confirmation} />

            {errors.general && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 13px', borderRadius: 9, background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', fontSize: 13, color: '#DC2626', fontWeight: 500 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {errors.general}
              </div>
            )}

            <button type="submit" disabled={loading} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '13px 20px', borderRadius: 11, border: 'none', marginTop: 4,
              background: loading ? 'var(--border)' : 'var(--brand-grad)',
              color: loading ? 'var(--text3)' : 'white',
              fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', transition: 'all 0.15s', minHeight: 48,
              boxShadow: loading ? 'none' : '0 4px 16px var(--accent-ring)',
            }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = 'brightness(1.08)'; }}
              onMouseLeave={e => e.currentTarget.style.filter = ''}
            >
              {loading ? <><Spinner />Creating account…</> : 'Create account →'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: 'var(--text3)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 700, textDecoration: 'none' }}>Sign in</Link>
          </p>
        </div>

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: 'var(--text3)' }}>
          <Link to="/app" style={{ color: 'var(--text3)', textDecoration: 'none' }}>
            Skip sign up — continue as guest →
          </Link>
        </p>
      </div>
    </div>
  );
}